import { useEffect, Fragment, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";

import { Header } from "../Header";
import { MessageList } from "../MessageList";
import { MessageInput } from "../MessageInput";
import { AppState } from "../../store/definitions";
import { ConversationEnded } from "../ConversationEnded";
import { NotificationBar } from "../NotificationBar";
import { removeNotification } from "../../store/actions/genericActions";
import { notifications } from "../../notifications";
import { AttachFileDropArea } from "../AttachFileDropArea";
import { reportChatPresence } from "../../utils/chatPresence";

const PRESENCE_HEARTBEAT_MS = 12_000;

export const MessagingPhase = () => {
    const dispatch = useDispatch();
    const conversationState = useSelector((state: AppState) => state.chat.conversationState);
    const conversationSid = useSelector((state: AppState) => state.session.conversationSid);
    const openId = useSelector((state: AppState) => state.session.preEngagementData?.TWLO_WeChatId__c);
    const serviceType = useSelector((state: AppState) => state.session.preEngagementData?.ServiceType);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        dispatch(removeNotification(notifications.failedToInitSessionNotification("ds").id));
    }, [dispatch]);

    // 上报是否仍在聊天窗：可见=active，切走/关闭=away；心跳防止漏报 away
    useEffect(() => {
        if (!conversationSid) {
            return undefined;
        }

        const send = (status: "active" | "away") => {
            reportChatPresence({
                conversationSid,
                openId,
                serviceType,
                status
            });
        };

        const syncByVisibility = () => {
            if (document.hidden) {
                send("away");
            } else {
                send("active");
            }
        };

        syncByVisibility();

        const onVisibility = () => syncByVisibility();
        const onPageHide = () => send("away");
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("beforeunload", onPageHide);

        heartbeatRef.current = setInterval(() => {
            if (!document.hidden) {
                send("active");
            }
        }, PRESENCE_HEARTBEAT_MS);

        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("beforeunload", onPageHide);
            if (heartbeatRef.current) {
                clearInterval(heartbeatRef.current);
                heartbeatRef.current = null;
            }
            send("away");
        };
    }, [conversationSid, openId, serviceType]);

    const Wrapper = conversationState === "active" ? AttachFileDropArea : Fragment;

    return (
        <Wrapper>
            <Box
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100dvh",
                    maxHeight: "100dvh",
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    position: "relative",
                    boxSizing: "border-box"
                }}
            >
                <Box style={{ flexShrink: 0, position: "sticky", top: 0, zIndex: 20 }}>
                    <Header />
                </Box>
                <NotificationBar />
                <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <MessageList />
                    {conversationState === "active" ? (
                        <MessageInput />
                    ) : (
                        <Box style={{ flexShrink: 0, maxHeight: "55%", overflow: "auto" }}>
                            <ConversationEnded />
                        </Box>
                    )}
                </Box>
            </Box>
        </Wrapper>
    );
};
